import type { AdminStatsDto } from '@/core/data/admin/admin.interface';
import type { AdminDetailLocationDto } from '@/core/data/location/location.interface';
import type { AdminReportDto } from '@/core/data/report/report.interface';
import type { AdminUserDto, AdminUsernameIssueDto } from '@/core/data/user/user.interface';
import { useFilterStore } from '@/stores/filter.store';
import { useTableStore } from '@/stores/table.store';
import type { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { AxiosResponse } from 'axios';
import type { HttpClient } from '../http/httpClient';
import type { GamResponse, GetTableParams } from '../http/httpClient.interface';
import { adminLocationFields, adminUserFields, detailsLocationFields } from './constants/api.fields.constant';
import { AdminEndpoint } from './constants/api.url.constant';
import { adminGambitFields } from './constants/gambit.api_fields';

export class AdminApi {
  private readonly httpClient: HttpClient;
  private readonly defaultSortBy = '-createdAt';

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  private getGenericParams(id: GamListId): GetTableParams {
    const tableStore = useTableStore(id)();
    const filterStore = useFilterStore<GetTableParams>(id)();

    return {
      ...tableStore.tableParams,
      ...filterStore.getFilters(),
    };
  }
  async getSparks(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminUserDto[]>>> {
    return this.httpClient.get(AdminEndpoint.SPARKS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
          fields: adminGambitFields.join(','),
          sortBy: '-updatedAt',
        },
      },
    });
  }
  async patchSpark(id: string, payload: any): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.patch(AdminEndpoint.SPARK, {
      urlParams: {
        id,
      },
      data: payload,
    });
  }

  async moveUser(userId: string, oldSparkId: string, newSparkId: string): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.patch(AdminEndpoint.SPARK_MOVE, {
      urlParams: {
        id: userId,
      },
      data: { oldSparkId, newSparkId },
    });
  }

  async deleteSpark(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.delete(AdminEndpoint.SPARK, {
      urlParams: {
        id,
      },
    });
  }

  async getUsers(
    listId: GamListId,
    sortBy: string = this.defaultSortBy,
  ): Promise<AxiosResponse<GamResponse<AdminUserDto[]>>> {
    return this.httpClient.get(AdminEndpoint.USERS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
          fields: adminUserFields.join(','),
          sortBy,
        },
      },
    });
  }

  async getUsernameIssues(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminUsernameIssueDto[]>>> {
    const { search } = this.getGenericParams(listId);

    return this.httpClient.get(AdminEndpoint.USERNAME_ISSUES, {
      config: {
        params: { search },
      },
    });
  }

  async getLocations(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminDetailLocationDto[]>>> {
    return this.httpClient.get(AdminEndpoint.LOCATIONS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
          fields: adminLocationFields.join(','),
          sortBy: this.defaultSortBy,
        },
      },
    });
  }

  async getLocationById(id: string): Promise<AxiosResponse<GamResponse<AdminDetailLocationDto>>> {
    return this.httpClient.get(AdminEndpoint.LOCATION, {
      urlParams: {
        id,
      },
      config: {
        params: {
          fields: detailsLocationFields.join(','),
        },
      },
    });
  }

  async verifyLocation(id: string, verified: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.put(AdminEndpoint.LOCATION, {
      urlParams: {
        id,
      },
      data: {
        verified,
      },
    });
  }

  async vetLocation(id: string, isVetted: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.put(AdminEndpoint.LOCATION, {
      urlParams: {
        id,
      },
      data: {
        isVetted,
      },
    });
  }

  async sparkLocation(id: string, isSparked: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.put(AdminEndpoint.LOCATION, {
      urlParams: {
        id,
      },
      data: {
        isSparked,
      },
    });
  }

  async updateLocation(location: AdminDetailLocationDto): Promise<AxiosResponse<GamResponse<void>>> {
    const { openingHours, ...rest } = location;
    return this.httpClient.put(AdminEndpoint.LOCATION, {
      urlParams: {
        id: location.id,
      },
      data: {
        ...rest,
        ...(openingHours !== undefined && { openingHours: JSON.stringify(openingHours) }),
      },
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async deleteLocation(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.delete(AdminEndpoint.LOCATION, {
      urlParams: {
        id,
      },
    });
  }

  async deleteUser(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.delete(AdminEndpoint.USER, {
      urlParams: {
        id,
      },
    });
  }

  async setUserGambitsMaster(id: string, isGambitsMaster: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.patch(AdminEndpoint.USER_ADMIN_FLAGS, {
      urlParams: {
        id,
      },
      data: {
        isGambitsMaster,
      },
    });
  }

  async fetchStats(): Promise<AxiosResponse<GamResponse<AdminStatsDto>>> {
    return this.httpClient.get(AdminEndpoint.STATS);
  }

  async getLocationReports(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminReportDto[]>>> {
    return this.httpClient.get(AdminEndpoint.REPORT_LOCATIONS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
        },
      },
    });
  }

  async getLocationCommentReports(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminReportDto[]>>> {
    return this.httpClient.get(AdminEndpoint.REPORT_LOCATION_COMMENTS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
        },
      },
    });
  }

  async getBlobReports(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminReportDto[]>>> {
    return this.httpClient.get(AdminEndpoint.REPORT_BLOBS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
        },
      },
    });
  }

  async getUserReports(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminReportDto[]>>> {
    return this.httpClient.get(AdminEndpoint.REPORT_USERS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
        },
      },
    });
  }

  async getGambitReports(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminReportDto[]>>> {
    return this.httpClient.get(AdminEndpoint.REPORT_GAMBITS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
        },
      },
    });
  }

  async getClubReports(listId: GamListId): Promise<AxiosResponse<GamResponse<AdminReportDto[]>>> {
    return this.httpClient.get(AdminEndpoint.REPORT_CLUBS, {
      config: {
        params: {
          ...this.getGenericParams(listId),
        },
      },
    });
  }
}
